import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { RouteLocationRaw } from 'vue-router'
import { Actions, Mutations } from '@/store/enums/StoreEnums'
import ApiService from '@/services/ApiService'
import {
  clearAllAuthenticationRedirectContexts,
  readActiveAuthenticationRedirectContext,
  resolvePostAuthenticationTarget
} from '@/services/AuthenticationRedirectContext'
import { ApiResponse } from '@/types/api-response'
import { savePostLogoutRedirectTarget } from '@/services/PostLogoutRedirect'

interface LoggedUserPayload {
  token: string
  userType: string
  userId: string
}

interface User {
  userType: string | null
  token?: string
  userId?: string
  displayName?: string | null
  // Destination de sortie du connect-as, validée par le serveur au moment du connect-as.
  connectAsExitTo?: string | null
  originalUser?: {
    token: string
    userType: string
    userId: string
    displayName?: string | null
  }
}

// eslint-disable-next-line camelcase
interface LogoutResponse {
  // eslint-disable-next-line camelcase
  adfs_logout_url?: string
  // eslint-disable-next-line camelcase
  redirect_url?: string | null
}

@Module
export default class UserModule extends VuexModule {
  user: User = {
    userType: null
  }

  // noinspection JSUnusedGlobalSymbols
  get getUser () {
    return this.user
  }

  // noinspection JSUnusedGlobalSymbols
  get isLoggedIn () {
    return this.user.userType !== null
  }

  // noinspection JSUnusedGlobalSymbols
  get isConnectedAs () {
    return !!this.user.originalUser
  }

  // noinspection JSUnusedGlobalSymbols
  get getConnectAsExitTo (): string | null {
    const exitTo = this.user.connectAsExitTo
    return typeof exitTo === 'string' && exitTo !== '' ? exitTo : null
  }

  // noinspection JSUnusedGlobalSymbols
  get getOriginalUser () {
    return this.user.originalUser || null
  }

  @Mutation
  [Mutations.SAVE_LOGGED_USER] (payload: { token: string; userType: string; userId: string; displayName?: string | null; connectAsExitTo?: string | null; originalUser?: { userId: string; userType: string; token: string; displayName?: string | null } }) {
    const connectAsExitTo = payload.originalUser ? (payload.connectAsExitTo ?? null) : null
    this.user = {
      userType: payload.userType,
      token: payload.token,
      userId: payload.userId,
      displayName: payload.displayName ?? null,
      connectAsExitTo,
      originalUser: payload.originalUser
    }
    if (connectAsExitTo !== null && connectAsExitTo !== '') {
      localStorage.setItem('connectAsExitTo', connectAsExitTo)
    } else {
      localStorage.removeItem('connectAsExitTo')
    }
    if (payload.displayName) {
      localStorage.setItem('connectAsDisplayName', payload.displayName)
    } else {
      localStorage.removeItem('connectAsDisplayName')
    }
    // Sauvegarder le token dans le localStorage
    localStorage.setItem('auth_token', payload.token)
    localStorage.setItem('userType', payload.userType)
    localStorage.setItem('userId', payload.userId)
    if (payload.originalUser) {
      localStorage.setItem('originalUser', JSON.stringify(payload.originalUser))
    }
  }

  @Mutation
  [Mutations.SAVE_CONNECT_AS_USER] (payload: { token: string; userType: string; userId: string; displayName?: string | null; connectAsExitTo?: string | null; originalUser: { userId: string; userType: string; token: string; displayName?: string | null } }) {
    // Si l'utilisateur est déjà en mode connect-as, on revient d'abord à l'utilisateur original
    if (this.user.originalUser) {
      // Restaurer l'utilisateur original avant de faire le nouveau connect-as
      this.user = {
        userType: this.user.originalUser.userType,
        token: this.user.originalUser.token,
        userId: this.user.originalUser.userId
      }
    }

    // L'API nous fournit déjà les informations de l'utilisateur original
    // On utilise le token actuel pour l'utilisateur original
    const originalUser = {
      token: this.user.token || payload.originalUser.token,
      userType: payload.originalUser.userType,
      userId: payload.originalUser.userId,
      displayName: payload.originalUser.displayName ?? null
    }

    this.user = {
      userType: payload.userType,
      token: payload.token,
      userId: payload.userId,
      displayName: payload.displayName ?? null,
      // Valeur déjà validée par le serveur : jamais la query brute.
      connectAsExitTo: payload.connectAsExitTo ?? null,
      originalUser: originalUser
    }
    if (typeof payload.connectAsExitTo === 'string' && payload.connectAsExitTo !== '') {
      localStorage.setItem('connectAsExitTo', payload.connectAsExitTo)
    } else {
      localStorage.removeItem('connectAsExitTo')
    }
    if (payload.displayName) {
      localStorage.setItem('connectAsDisplayName', payload.displayName)
    } else {
      localStorage.removeItem('connectAsDisplayName')
    }

    // Sauvegarder les nouvelles informations dans le localStorage
    localStorage.setItem('auth_token', payload.token)
    localStorage.setItem('userType', payload.userType)
    localStorage.setItem('userId', payload.userId)
    localStorage.setItem('originalUser', JSON.stringify(originalUser))
  }

  @Mutation
  [Mutations.EXIT_CONNECT_AS_USER] () {
    // Restaurer l'utilisateur original
    if (this.user.originalUser) {
      const originalUser = this.user.originalUser
      this.user = {
        userType: originalUser.userType,
        token: originalUser.token,
        userId: originalUser.userId
      }

      // Restaurer les informations dans le localStorage
      localStorage.setItem('auth_token', originalUser.token)
      localStorage.setItem('userType', originalUser.userType)
      localStorage.setItem('userId', originalUser.userId)
      localStorage.removeItem('connectedAs')
      localStorage.removeItem('originalUser')
      localStorage.removeItem('connectAsDisplayName')
      localStorage.removeItem('connectAsExitTo')
    }
  }

  @Mutation
  [Mutations.LOGOUT_CURRENT_USER] () {
    this.user = {
      userType: null
    }
    // Nettoyer le localStorage
    localStorage.removeItem('auth_token')
    localStorage.removeItem('userType')
    localStorage.removeItem('userId')
    localStorage.removeItem('connectedAs')
    localStorage.removeItem('originalUser')
    localStorage.removeItem('connectAsDisplayName')
    localStorage.removeItem('connectAsExitTo')
    // Les contextes de redirection ne sont PAS effacés ici. Cette mutation sert deux
    // appelants que tout oppose : la déconnexion voulue, et le 401 que l'intercepteur
    // d'`ApiService` traduit par « la session a expiré ». Dans ce second cas la personne
    // est en train d'arriver depuis une application appelante, et va se reconnecter dans
    // la seconde : lui retirer sa destination la laissait sur le sélecteur de services au
    // lieu de la ramener à l'application qui l'attend. La purge appartient donc à la
    // déconnexion voulue seule (cf. `Actions.LOGOUT_USER`).
    // Supprimer le header Authorization
    if (ApiService.vueInstance?.axios) {
      delete ApiService.vueInstance.axios.defaults.headers.common.Authorization
    }
  }

  /**
   * Déconnecte l'utilisateur et renvoie `true` si la suite se joue chez le
   * fournisseur d'identité externe (redirection ADFS déjà posée sur `window.location`),
   * `false` sinon. L'appelant en a besoin pour savoir s'il lui reste une redirection
   * post-déconnexion à honorer, ou si celle-ci est sans objet.
   *
   * `payload.postLogoutRedirectUri`, quand fourni, part en paramètre de requête de
   * l'appel de déconnexion : c'est l'API — endpoint désormais public, joignable sans
   * jeton ou avec un jeton invalide — qui décide si cette origine est un client OAuth
   * enregistré, et renvoie alors `redirect_url`. Ce dernier est mémorisé AVANT la
   * redirection ADFS éventuelle, pour survivre au détour par le fournisseur d'identité.
   */
  @Action
  async [Actions.LOGOUT_USER] (payload?: { postLogoutRedirectUri?: string | null }): Promise<boolean> {
    const token = localStorage.getItem('auth_token')
    let adfsLogoutUrl: string | null = null
    const isDevEnv = process.env.VUE_APP_ENV === 'dev' || process.env.VUE_APP_ENV === 'local'
    const forceProductionMode = localStorage.getItem('forceProductionMode') === 'true'

    try {
      // Appeler l'API de logout, désormais accessible sans jeton : elle doit se jouer
      // même pour un utilisateur non connecté ou dont la session a déjà expiré.
      const response = await ApiService.get<ApiResponse<LogoutResponse>>('/authentication/v1.0/logout', {
        // eslint-disable-next-line camelcase
        params: { post_logout_redirect_uri: payload?.postLogoutRedirectUri || undefined },
        headers: token
          ? { Authorization: `Bearer ${token}` }
          : {}
      })

      // Récupérer l'URL de logout ADFS si présente (l'API renvoie adfs_logout_url en snake_case)
      // eslint-disable-next-line camelcase
      adfsLogoutUrl = response.data.data.adfs_logout_url || null

      // eslint-disable-next-line camelcase
      const redirectUrl = response.data.data.redirect_url || null
      if (redirectUrl !== null) {
        savePostLogoutRedirectTarget(redirectUrl)
      }
    } catch (error) {
      console.error('Erreur lors du logout:', error)
      // Continuer le logout même en cas d'erreur API
    } finally {
      // Toujours exécuter la mutation pour nettoyer le store et localStorage
      this.context.commit(Mutations.LOGOUT_CURRENT_USER)

      // Déconnexion VOULUE : c'est ici, et nulle part ailleurs, que les contextes de
      // redirection tombent. Un contexte survivant enverrait la personne suivante vers
      // l'application de la précédente — ce que la mutation ne peut pas décider seule,
      // puisqu'une session expirée l'appelle aussi.
      clearAllAuthenticationRedirectContexts()
    }

    // Si utilisateur ADFS, rediriger vers ADFS logout
    // SAUF en mode dev (à moins que le mode production soit forcé)
    const shouldRedirectToIdentityProvider = adfsLogoutUrl !== null && !(isDevEnv && !forceProductionMode)
    if (shouldRedirectToIdentityProvider) {
      window.location.href = adfsLogoutUrl as string
    }

    return shouldRedirectToIdentityProvider
  }

  @Action
  [Actions.CONFIGURE_AXIOS_AUTHENTICATION] (payload: string) {
    ApiService.vueInstance.axios.defaults.headers.common.Authorization = 'Bearer ' + payload
  }

  /**
   * Séquence commune à toute authentification réussie (login, magic link, première
   * connexion, réinitialisation de mot de passe) : enregistrer l'utilisateur, configurer
   * Axios avec son jeton, puis résoudre la destination post-authentification à partir du
   * contexte de redirection actif. Un seul endroit décide de cette destination, et il lit
   * le contexte, pas l'URL : à l'appelant de pousser la route retournée.
   */
  @Action
  async [Actions.COMPLETE_AUTHENTICATION_AND_RESOLVE_POST_AUTHENTICATION_TARGET] (payload: LoggedUserPayload): Promise<RouteLocationRaw> {
    this.context.commit(Mutations.SAVE_LOGGED_USER, payload)

    if (payload.token) {
      await this.context.dispatch(Actions.CONFIGURE_AXIOS_AUTHENTICATION, payload.token)
    }

    return resolvePostAuthenticationTarget(readActiveAuthenticationRedirectContext())
  }

  @Action
  restoreSession () {
    const token = localStorage.getItem('auth_token')
    const userType = localStorage.getItem('userType')
    const userId = localStorage.getItem('userId')
    const originalUserStr = localStorage.getItem('originalUser')
    const displayName = localStorage.getItem('connectAsDisplayName')
    const connectAsExitTo = localStorage.getItem('connectAsExitTo')

    if (token) {
      // Préparer le payload
      interface SaveLoggedUserPayload {
        token: string;
        userType: string | null;
        userId: string | null;
        displayName?: string | null;
        connectAsExitTo?: string | null;
        originalUser?: { userId: string; userType: string; token: string; displayName?: string | null };
      }

      const payload: SaveLoggedUserPayload = {
        token,
        userType: userType || null,
        userId: userId || null,
        displayName,
        connectAsExitTo
      }

      // Ajouter originalUser si présent
      if (originalUserStr) {
        try {
          payload.originalUser = JSON.parse(originalUserStr)
        } catch (e) {
          console.error('Erreur lors du parsing de originalUser:', e)
        }
      }

      // Restaurer l'utilisateur dans le store
      this.context.commit(Mutations.SAVE_LOGGED_USER, payload)

      // Configurer Axios avec le token
      this.context.dispatch(Actions.CONFIGURE_AXIOS_AUTHENTICATION, token)
    }
  }

  @Action
  async [Actions.EXIT_CONNECT_AS] (payload?: { returnTo?: string }): Promise<string | null> {
    // Seule la valeur renvoyée par l'API (validée côté serveur) est retournée, jamais celle du payload.
    let validatedReturnTo: string | null = null

    // Révoquer côté serveur le connect-as AVANT de restaurer l'utilisateur original : le jeton
    // à présenter est celui du connect-as courant, que la restauration va écraser.
    if (this.user.originalUser && this.user.token) {
      try {
        const requestedReturnTo = payload?.returnTo
        const response = await ApiService.post<
          // eslint-disable-next-line camelcase
          { return_to?: string },
          // eslint-disable-next-line camelcase
          ApiResponse<{ return_to?: string | null }>
        >(
          '/authentication/v1.0/connect-as/exit',
          requestedReturnTo !== undefined && requestedReturnTo !== '' ? { return_to: requestedReturnTo } : {},
          { headers: { Authorization: `Bearer ${this.user.token}` } }
        )
        const returnedValue = response.data?.data?.return_to
        validatedReturnTo = typeof returnedValue === 'string' && returnedValue !== '' ? returnedValue : null
      } catch (error) {
        console.error('Erreur lors de la révocation du connect-as:', error)
        // Continuer la sortie locale même en cas d'erreur API
      }
    }

    // Restaurer l'utilisateur original
    this.context.commit(Mutations.EXIT_CONNECT_AS_USER)

    // Reconfigurer Axios avec le token original
    const originalToken = this.user.token
    if (originalToken) {
      this.context.dispatch(Actions.CONFIGURE_AXIOS_AUTHENTICATION, originalToken)
    }

    return validatedReturnTo
  }
}
